import { Alert, Button, Hint, Input, Label, Text } from "@medusajs/ui"
import { AuthShell } from "@/components/auth-shell"
import { useState, type FormEvent } from "react"
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { useAcceptInvite, useInviteLookup, useLogin, useMe, useRequestPasswordReset, useResetPassword } from "@/lib/api"
import { errorMessage } from "@/lib/sdk"

/** Shared frame for sign-in, invitation and password pages (components/auth-shell.tsx). */
const AuthCard = AuthShell

function Field({ id, label, ...props }: { id: string; label: string } & React.ComponentProps<typeof Input>) {
  return (
    <div className="flex flex-col gap-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
    </div>
  )
}

const REASONS: Record<string, string> = {
  session_revoked: "You were signed out by a store admin. Sign in again to continue.",
  account_deactivated: "This staff account has been deactivated. Ask the store owner to reactivate it.",
}

export function LoginPage() {
  const { data: me } = useMe()
  const login = useLogin()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState(params.get("email") ?? "")
  const [password, setPassword] = useState("")
  const [caps, setCaps] = useState(false)
  const next = params.get("next")?.startsWith("/") ? params.get("next")! : "/"
  const notice = params.get("reason") ? REASONS[params.get("reason")!] : params.get("welcome") ? "Your account is ready. Sign in to get started." : params.get("reset") ? "Password updated. Sign in with your new password." : null

  if (me?.staff) return <Navigate to={next} replace />

  const submit = (e: FormEvent) => {
    e.preventDefault()
    login.mutate({ email, password }, { onSuccess: () => navigate(next, { replace: true }) })
  }

  return (
    <AuthCard title="Sign in to Keel" subtitle="Use your staff email and password.">
      {notice && (
        <Alert variant="info" className="mb-4">
          {notice}
        </Alert>
      )}
      <form onSubmit={submit} className="flex flex-col gap-y-4">
        <Field id="email" label="Email" type="email" autoComplete="username email" autoFocus={!email} required value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="flex flex-col gap-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`} className="txt-compact-xsmall-plus text-ui-fg-interactive">
              Forgot password?
            </Link>
          </div>
          <Input id="password" type="password" autoComplete="current-password" autoFocus={!!email} required value={password} onChange={(e) => setPassword(e.target.value)} onKeyUp={(e) => setCaps(e.getModifierState?.("CapsLock") ?? false)} />
          {caps ? <Text size="xsmall" className="text-ui-fg-subtle">Caps Lock is on.</Text> : null}
        </div>
        {login.error && <Hint variant="error">{(login.error as any)?.status === 401 ? "Wrong email or password." : errorMessage(login.error)}</Hint>}
        <Button type="submit" isLoading={login.isPending} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthCard>
  )
}

export function AcceptInvitePage() {
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""
  const navigate = useNavigate()
  const lookup = useInviteLookup(token)
  const accept = useAcceptInvite()
  const [first, setFirst] = useState<string | null>(null)
  const [last, setLast] = useState<string | null>(null)
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [mismatch, setMismatch] = useState(false)

  if (!token || lookup.error) {
    return (
      <AuthCard title="Invitation not valid" subtitle={lookup.error ? errorMessage(lookup.error) : "This link is missing its invitation code."}>
        <Button variant="secondary" className="w-full" onClick={() => navigate("/login")}>
          Go to sign in
        </Button>
      </AuthCard>
    )
  }
  if (lookup.isLoading || !lookup.data) return <AuthCard title="Checking your invitation…">{null}</AuthCard>

  const { invite, store } = lookup.data
  const firstName = first ?? invite.first_name ?? ""
  const lastName = last ?? invite.last_name ?? ""

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (password !== confirm) return setMismatch(true)
    setMismatch(false)
    accept.mutate(
      { token, first_name: firstName, last_name: lastName, password },
      { onSuccess: ({ email }) => navigate(`/login?welcome=1&email=${encodeURIComponent(email)}`, { replace: true }) }
    )
  }

  return (
    <AuthCard
      title={`Join ${store.name}`}
      subtitle={
        <>
          {invite.invited_by_email ?? "The store owner"} invited <span className="text-ui-fg-base">{invite.email}</span> to the staff team. Create your account to accept.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field id="first" label="First name" required value={firstName} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" />
          <Field id="last" label="Last name" required value={lastName} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" />
        </div>
        <Field id="password" label="Password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        <Field id="confirm" label="Confirm password" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        <Text size="xsmall" className="text-ui-fg-subtle">
          At least 8 characters.
        </Text>
        {mismatch && <Hint variant="error">The passwords don't match.</Hint>}
        {accept.error && <Hint variant="error">{errorMessage(accept.error)}</Hint>}
        <Button type="submit" isLoading={accept.isPending} className="w-full">
          Accept invitation
        </Button>
      </form>
    </AuthCard>
  )
}

export function ForgotPasswordPage() {
  const [params] = useSearchParams()
  const [email, setEmail] = useState(params.get("email") ?? "")
  const request = useRequestPasswordReset()

  if (request.isSuccess) {
    return (
      <AuthCard title="Check your email" subtitle={`If ${email} has a staff account, we've sent a link to reset the password. It expires in 15 minutes.`}>
        <Link to="/login" className="txt-compact-small-plus block text-center text-ui-fg-interactive">
          Back to sign in
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard title="Reset your password" subtitle="Enter your staff email and we'll send you a reset link.">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          request.mutate(email)
        }}
        className="flex flex-col gap-y-4"
      >
        <Field id="email" label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        {request.error && <Hint variant="error">{errorMessage(request.error)}</Hint>}
        <Button type="submit" isLoading={request.isPending} className="w-full">
          Send reset link
        </Button>
        <Link to="/login" className="txt-compact-small-plus text-center text-ui-fg-interactive">
          Back to sign in
        </Link>
      </form>
    </AuthCard>
  )
}

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""
  const email = params.get("email") ?? ""
  const navigate = useNavigate()
  const reset = useResetPassword()
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [mismatch, setMismatch] = useState(false)

  if (!token || !email) {
    return (
      <AuthCard title="Link not valid" subtitle="This reset link is incomplete. Ask for a new one.">
        <Button variant="secondary" className="w-full" onClick={() => navigate("/forgot-password")}>
          Request a new link
        </Button>
      </AuthCard>
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (password !== confirm) return setMismatch(true)
    setMismatch(false)
    reset.mutate({ token, email, password }, { onSuccess: () => navigate(`/login?reset=1&email=${encodeURIComponent(email)}`, { replace: true }) })
  }

  return (
    <AuthCard title="Choose a new password" subtitle={email}>
      <form onSubmit={submit} className="flex flex-col gap-y-4">
        <Field id="password" label="New password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        <Field id="confirm" label="Confirm new password" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        {mismatch && <Hint variant="error">The passwords don't match.</Hint>}
        {reset.error && (
          <Hint variant="error">{(reset.error as any)?.status === 401 ? "This link has expired. Ask for a new one." : errorMessage(reset.error)}</Hint>
        )}
        <Button type="submit" isLoading={reset.isPending} className="w-full">
          Update password
        </Button>
      </form>
    </AuthCard>
  )
}
